import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useI18n } from "@/lib/i18n";
import { TID } from "@/lib/testIds";
import { ArrowUpRight, ArrowDown, ArrowRight } from "lucide-react";

const HERO_VIDEO =
  "https://videos.pexels.com/video-files/3129957/3129957-uhd_2560_1440_30fps.mp4";
const HERO_FALLBACK =
  "https://images.unsplash.com/photo-1469981283837-561b3779462f?auto=format&fit=crop&w=2400&q=80";

export default function Hero() {
  const { t } = useI18n();
  const marquee = t("marquee");
  const marqueeItems = Array.isArray(marquee) ? marquee : [];

  return (
    <section
      data-testid={TID.hero}
      className="relative w-full overflow-hidden bg-[color:var(--gc-navy)]"
      style={{ minHeight: "100vh" }}
    >
      {/* Video / image background */}
      <div className="absolute inset-0 z-0">
        <video
          className="w-full h-full object-cover gc-kenburns"
          autoPlay
          loop
          muted
          playsInline
          poster={HERO_FALLBACK}
          style={{ filter: "brightness(0.45) contrast(1.05) saturate(0.85)" }}
        >
          <source src={HERO_VIDEO} type="video/mp4" />
        </video>
        <div className="absolute inset-0 bg-[color:var(--gc-navy)]/55" />
        <div className="absolute inset-0 bg-gradient-to-r from-[color:var(--gc-navy)] via-[color:var(--gc-navy)]/75 to-[color:var(--gc-navy)]/35" />
        <div className="absolute inset-0 bg-gradient-to-b from-[color:var(--gc-navy)]/50 via-transparent to-[color:var(--gc-navy)]" />
        <div className="absolute inset-0 gc-grid-bg-dark opacity-50" />
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-[1440px] mx-auto px-6 lg:px-10 pt-36 pb-20 lg:pt-44 lg:pb-28 min-h-screen flex flex-col justify-between">
        <div className="flex-1 flex flex-col justify-center">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="gc-eyebrow text-white/80 mb-8"
          >
            {t("hero.eyebrow")}
          </motion.div>

          <div className="max-w-5xl">
            <motion.h1
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.1 }}
              className="font-display text-white text-[10vw] leading-[0.92] tracking-tight font-[700] sm:text-6xl md:text-7xl lg:text-8xl xl:text-[112px]"
              style={{ textShadow: "0 2px 32px rgba(10,17,40,0.55), 0 1px 2px rgba(0,0,0,0.45)" }}
            >
              <span className="block">{t("hero.title_line1")}</span>
              <span className="block text-white/85">{t("hero.title_line2")}</span>
              <span className="block">
                <span className="relative inline-block">
                  {t("hero.title_line3")}
                  <span className="absolute -bottom-2 left-0 h-[6px] w-3/4 bg-[color:var(--gc-red)]" />
                </span>
              </span>
            </motion.h1>
          </div>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.3 }}
            className="mt-10 max-w-2xl text-white/95 text-lg md:text-xl leading-relaxed"
            style={{ textShadow: "0 1px 20px rgba(10,17,40,0.6)" }}
          >
            {t("hero.subtitle")}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="mt-10 flex flex-wrap gap-3"
          >
            <Link
              data-testid={TID.heroCtaPrimary}
              to="/contact"
              className="gc-btn gc-btn-primary bg-white text-[color:var(--gc-ink)] hover:bg-white/90"
            >
              <span>{t("common.cta_specialist")}</span>
              <ArrowUpRight className="w-4 h-4 gc-arrow" />
            </Link>
            <Link
              data-testid={TID.heroCtaSecondary}
              to="/solutii"
              className="gc-btn gc-btn-ghost"
            >
              <span>{t("common.cta_solutions")}</span>
              <ArrowRight className="w-4 h-4 gc-arrow" />
            </Link>
          </motion.div>
        </div>

        {/* Stats row */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.7 }}
          className="mt-16 grid grid-cols-1 md:grid-cols-4 gap-px bg-white/10 border border-white/10"
        >
          <div className="bg-[color:var(--gc-navy)]/85 backdrop-blur-sm px-6 py-7">
            <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-white/60 mb-3">
              {t("hero.scroll")}
            </p>
            <ArrowDown className="w-5 h-5 text-white/70" />
          </div>
          {["stat_1", "stat_2", "stat_3"].map((k) => (
            <div key={k} className="bg-[color:var(--gc-navy)]/85 backdrop-blur-sm px-6 py-7">
              <p className="font-display text-3xl md:text-4xl text-white font-[700] tracking-tight">
                {t(`hero.${k}_value`)}
              </p>
              <p className="mt-2 text-white/65 text-sm">{t(`hero.${k}_label`)}</p>
            </div>
          ))}
        </motion.div>
      </div>

      {/* Marquee */}
      <div className="relative z-10 border-t border-white/10 bg-[color:var(--gc-navy)]/95 overflow-hidden">
        <div className="flex gap-12 py-5 gc-marquee-x whitespace-nowrap">
          {[...marqueeItems, ...marqueeItems].map((m, i) => (
            <span
              key={i}
              className="inline-flex items-center gap-3 font-mono text-[12px] uppercase tracking-[0.18em] text-white/55"
            >
              <span className="w-1.5 h-1.5 bg-[color:var(--gc-red)]" />
              {m}
            </span>
          ))}
        </div>
      </div>
    </section>
  );
}
